import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import TakeoffViewer, { computeCondMetrics } from "./TakeoffViewer";
import TakeoffEstimatePage from "./TakeoffEstimatePage";
import { BodyPortal } from "./FullscreenHost";

const API = "https://erp-xe9u.onrender.com/api";

// a saved takeoff document → the state the viewer starts from
const docToState = (doc) => ({
  conditions: doc.conditions || [],
  shapes: doc.shapes || [],
  scales: doc.scales || {},
  mappings: doc.mappings || [],
  excludedPages: doc.excludedPages || [],
  units: doc.units || [],
  scaleInfo: doc.scaleInfo || {},
  assemblies: doc.assemblies || [],
  traceSettings: doc.traceSettings || {},
  wallTagSettings: doc.wallTagSettings,
  proposals: doc.proposals || [],
  status: doc.status,
  pageNames: doc.pageNames || {},
  pageCount: doc.pageCount || 0,
  fileName: doc.fileName || "",
  gcsPath: doc.gcsPath || "",
  changeOrderId: doc.changeOrderId || null,
  _id: doc._id,
});

export default function COTakeoffLauncher({
  open,
  onClose,
  projectId,
  token,
  initialData,
  fileUrl,
  getCatalog,
  knownBidAreas = [],
  onIngestItems,
  onTakeoffSaved,
  changeOrderId, // ← add this prop so we can refetch by CO
}) {
  const headers = { Authorization: `Bearer ${token}` };
  const [screen, setScreen] = useState("draw");
  const [hydrated, setHydrated] = useState(false);

  const latestRef = useRef({
    conditions: initialData?.conditions || [],
    shapes: initialData?.shapes || [],
    scales: initialData?.scales || {},
    mappings: initialData?.mappings || [],
    excludedPages: initialData?.excludedPages || [],
    units: initialData?.units || [],
    scaleInfo: initialData?.scaleInfo || {},
    pageNames: initialData?.pageNames || {},
    fileName: initialData?.fileName || "",
    gcsPath: initialData?.gcsPath || "",
    _id: initialData?._id || null,
  });

  const [mappings, setMappings] = useState(initialData?.mappings || []);
  const [viewerKey, setViewerKey] = useState(0); // bump to remount the viewer on another takeoff
  const [title, setTitle] = useState(initialData?.fileName || "");
  const [estimateSnapshot, setEstimateSnapshot] = useState(null);

  // ── Rehydrate the FULL takeoff every time the launcher opens ─────────────
  // The parent's initialData can be stale (e.g. after a save + reopen), and
  // the link-takeoff response doesn't carry mappings. Fetch the source of
  // truth so conditions + mappings + shapes are always current.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        // Prefer CO key, fall back to fileName — matches the GET / route.
        const params = new URLSearchParams({ projectId });
        if (changeOrderId) params.set("changeOrderId", changeOrderId);
        else if (initialData?._id) params.set("id", initialData._id);
        else if (initialData?.fileName)
          params.set("fileName", initialData.fileName);
        else {
          setHydrated(true);
          return;
        } // a brand-new takeoff: nothing saved yet

        const res = await axios.get(`${API}/takeoffs3?${params.toString()}`, {
          headers,
        });
        const doc = res.data?.data?._id ? res.data.data : res.data;
        if (cancelled || !doc?._id) {
          setHydrated(true);
          return;
        }
        latestRef.current = docToState(doc);
        setTitle(doc.fileName || "");
        setMappings(doc.mappings || []);
      } catch (err) {
        // 404 = no takeoff saved yet; that's fine, keep initialData seed.
        if (err?.response?.status !== 404)
          console.error("Takeoff hydrate failed", err);
      } finally {
        if (!cancelled) setHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, changeOrderId, projectId]);

  // saved takeoffs on this project (shown when no drawing is open) and opening one of them
  const listTakeoffs = async () => {
    const res = await axios.get(`${API}/takeoffs3/project/${projectId}`, {
      headers,
    });
    return Array.isArray(res.data) ? res.data : res.data?.data || [];
  };
  const openTakeoff = async (id) => {
    const res = await axios.get(
      `${API}/takeoffs3?${new URLSearchParams({ projectId, id })}`,
      { headers },
    );
    const doc = res.data?.data?._id ? res.data.data : res.data;
    if (!doc?._id) return;
    latestRef.current = docToState(doc);
    setMappings(doc.mappings || []);
    setTitle(doc.fileName || "");
    setViewerKey((k) => k + 1);
    onTakeoffSaved?.(doc); // let the parent remember which takeoff is open
  };

  const handleSaveDrawing = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const res = await axios.post(
      `${API}/takeoffs3/upload-drawing?projectId=${projectId}`,
      form,
      { headers: { ...headers, "Content-Type": "multipart/form-data" } },
    );
    return res.data;
  };

  const handleSaveTakeoff = async (payload = {}) => {
    const nextMappings =
      payload.mappings !== undefined ? payload.mappings : mappings;

    // a blank name / id from the viewer never wipes out the one we already have
    const keep = (k) => (payload[k] ? payload[k] : latestRef.current[k]);
    latestRef.current = {
      ...latestRef.current,
      ...payload,
      fileName: keep("fileName") || "",
      gcsPath: keep("gcsPath") || "",
      _id: keep("_id") || null,
      mappings: nextMappings,
    };

    const body = {
      ...latestRef.current,
      projectId,
      changeOrderId: changeOrderId || latestRef.current.changeOrderId || null,
    };
    if (!body._id) delete body._id;

    const res = await axios.put(`${API}/takeoffs3`, body, { headers });
    const doc = res.data?.data?._id ? res.data.data : res.data;
    if (doc?.fileName) {
      latestRef.current.fileName = doc.fileName;
      setTitle(doc.fileName);
    }
    if (doc?._id) latestRef.current._id = doc._id;
    if (doc?.gcsPath) latestRef.current.gcsPath = doc.gcsPath;
    // Adopt the server's saved mappings so latestRef + state match the DB.
    if (Array.isArray(doc?.mappings)) {
      latestRef.current.mappings = doc.mappings;
      setMappings(doc.mappings);
    }
    onTakeoffSaved?.(doc);
    return doc;
  };

  const goEstimate = (viewerPayload) => {
    if (viewerPayload) {
      latestRef.current = {
        ...latestRef.current,
        ...viewerPayload,
        mappings: latestRef.current.mappings,
      };
    }
    const snap = latestRef.current;
    const condMetrics = {};
    (snap.conditions || []).forEach((c) => {
      condMetrics[c.id] = computeCondMetrics(
        c,
        snap.shapes || [],
        snap.scales || {},
        snap.excludedPages || [],
        snap.units || [],
      );
    });
    setEstimateSnapshot({ conditions: snap.conditions || [], condMetrics });
    setScreen("estimate");
  };

  if (!open) return null;

  // Rendered under <body> (not inside the tab), so it really covers the sidebar, header and tab bar.
  return (
    <BodyPortal>
      <div
        data-takeoff-shell
        className="fixed inset-0 z-[1300] flex flex-col bg-gray-950"
      >
        <div className="flex h-10 shrink-0 items-center justify-between border-b border-gray-800 bg-gray-900 px-3">
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="rounded-md px-2.5 py-1 text-xs font-medium text-gray-300 hover:bg-gray-800 hover:text-white"
            >
              ✕ Close
            </button>
            <span className="text-sm font-semibold text-white">
              Takeoff — {title || latestRef.current.fileName || "new drawing"}
            </span>
          </div>
          <div className="flex gap-1 rounded-lg bg-gray-800 p-1">
            <button
              onClick={() => setScreen("draw")}
              className={`rounded-md px-4 py-1.5 text-xs font-medium transition-colors ${
                screen === "draw"
                  ? "bg-gray-700 text-orange-400 shadow"
                  : "text-gray-400 hover:text-gray-200"
              }`}
            >
              Drawing
            </button>
            <button
              onClick={() => goEstimate()}
              className={`rounded-md px-4 py-1.5 text-xs font-medium transition-colors ${
                screen === "estimate"
                  ? "bg-gray-700 text-orange-400 shadow"
                  : "text-gray-400 hover:text-gray-200"
              }`}
            >
              Estimate
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 bg-gray-950">
          <div className={screen === "draw" ? "h-full" : "hidden"}>
            {" "}
            {/* keep mounted: map, sheets & proposals survive tab switches */}
            {hydrated && (
              <TakeoffViewer
                key={viewerKey}
                listTakeoffs={listTakeoffs}
                onOpenTakeoff={openTakeoff}
                inShell
                projectId={projectId}
                fileUrl={fileUrl}
                initialData={latestRef.current}
                onSave={handleSaveTakeoff}
                onUploadDrawing={handleSaveDrawing}
                getCatalog={getCatalog}
                onGoToEstimate={goEstimate}
              />
            )}
          </div>

          {screen === "estimate" && estimateSnapshot && (
            <TakeoffEstimatePage
              conditions={estimateSnapshot.conditions}
              condMetrics={estimateSnapshot.condMetrics}
              mappings={mappings}
              setMappings={setMappings}
              getCatalog={getCatalog}
              knownBidAreas={knownBidAreas}
              onSaveDraft={() =>
                handleSaveTakeoff({ ...latestRef.current, mappings })
              }
              onAddToChangeOrder={(coItems) => {
                onIngestItems?.(coItems);
                onClose?.();
              }}
              onBack={() => setScreen("draw")}
            />
          )}
        </div>
      </div>
    </BodyPortal>
  );
}
